import type { JSX } from "react";

import ContactGroupsPage from "@crm-src/pages/contact-groups/ContactGroupsPage";
import { useTabParam } from "@crm-src/hooks/useTabParam";

import ContactsPage from "./ContactsPage";
import { CONTACTS_HUB_TAB_IDS, ContactsHubTabs, type ContactsHubTabId } from "./ContactsHubTabs";

/**
 * CRM ▸ **Contacts & Groups** — ONE nav destination (`/crm/contacts`) with
 * two tabs, Contacts ∣ Groups, through the same `<DetailTabs>` + `useTabParam`
 * pair the detail pages use (`?tab=groups` deep-links the second tab; the
 * old `/crm/contact-groups` URL redirects there).
 *
 * This is deliberately a thin switch: each tab is still its own page
 * (`ContactsPage` / `ContactGroupsPage`) with its own header actions, data
 * loading and dialogs — the hub only hands each one the tab strip to render
 * under the shared "Contacts & Groups" title. Switching tabs unmounts the
 * other page, so neither one's list query runs while it is out of view.
 */
export default function ContactsHubPage(): JSX.Element {
  const [activeTab, setTab] = useTabParam(CONTACTS_HUB_TAB_IDS, "contacts");
  const tabs = (
    <ContactsHubTabs
      activeId={activeTab as ContactsHubTabId}
      onTabChange={(id) => setTab(id)}
    />
  );
  return activeTab === "groups" ? <ContactGroupsPage tabs={tabs} /> : <ContactsPage tabs={tabs} />;
}
